import React, { useEffect } from 'react';
import Connector, { PropsFromRedux } from './Connector';
import { injectIntl, IntlShape } from 'react-intl';
import {
  BaseCard,
  BaseCardHeader,
  StackedBarsWithLineComposedChart,
} from '@neteera/neteera-web-nextgen';
import { getBedOccupancyInterval } from 'src/routes/IndividualDashboard/modules/dashboard/utils';
import dayjs from 'dayjs';
import {
  calculateBedActivityTrendLineData,
  getDaysBetweenDates,
  getWidgetConfig,
  messages,
} from './utils';
import { TimeFrame } from 'src/routes/IndividualDashboard/modules/dashboard/types';
import { UUID } from 'src/types/utility';
import { StyledDescription } from './styled';
import { CUSTOMER_FEATURE_SETTINGS } from 'src/routes/Customers/modules/types';
import { isNullOrUndefined } from 'src/utils/parser';
import {
  API_DATE_FORMAT,
  displayInTimezone,
  getFormatDateByContinent,
  HOUR_12H_FORMAT,
  MONTH_DAY_FORMAT,
  MONTH_DAY_YEAR_FORMAT,
} from 'src/utils/timeUtils';

type Props = PropsFromRedux & {
  intl: IntlShape;
  timeFrame?: Omit<TimeFrame, 'range'>;
  patientIdProp?: UUID;
};
const BedActivity = ({
  intl,
  patient,
  patientIdProp,
  getSleepData,
  getMotionData,
  sleepData,
  motionData,
  clearSleepDataGraph,
  clearBedOccupancyGraph,
  hoursInBedData,
  availableFeatures,
  timeFrame,
  isLoading,
  getSiteFeatureSettings,
  sleepWakeSettings,
  timezone,
}: Props) => {
  const patientId = patient?.id ?? patientIdProp;
  const { SLEEP_WAKE: hasSleepWakeFeature = false } = availableFeatures;
  const { startTime, endTime } = timeFrame ?? getBedOccupancyInterval();
  useEffect(() => {
    getSiteFeatureSettings({
      featureName: CUSTOMER_FEATURE_SETTINGS.SLEEP_WAKE,
    });
  }, []);

  useEffect(() => {
    if (
      !patientId ||
      isNullOrUndefined(sleepWakeSettings?.startHour) ||
      isNullOrUndefined(sleepWakeSettings?.endHour) ||
      !startTime ||
      !endTime
    ) {
      return;
    }
    const updatedStartTime = dayjs(startTime)
      .hour(sleepWakeSettings?.startHour ?? 12)
      .format(API_DATE_FORMAT);

    const updatedEndTime = dayjs(endTime)
      .add(1, 'day')
      .hour(sleepWakeSettings?.endHour ?? 7)
      .minute(0)
      .second(0)
      .millisecond(0)
      .format(API_DATE_FORMAT);

    if (hasSleepWakeFeature) {
      getSleepData({
        patientId,
        startTime: updatedStartTime,
        endTime: updatedEndTime,
      });
    }
    getMotionData({
      patientId,
      startTime: updatedStartTime,
      endTime: dayjs(endTime)
        .add(1, 'day')
        .hour(sleepWakeSettings?.startHour ?? 12)
        .minute(0)
        .second(0)
        .millisecond(0)
        .format(API_DATE_FORMAT),
    });

    return () => {
      clearSleepDataGraph();
      clearBedOccupancyGraph({ patientId });
    };
  }, [
    patientId,
    hasSleepWakeFeature,
    startTime,
    endTime,
    sleepWakeSettings?.startHour,
    sleepWakeSettings?.endHour,
  ]);

  const daysArray = getDaysBetweenDates(startTime, endTime);
  const hoursInBedPerDay = calculateBedActivityTrendLineData(hoursInBedData);
  return (
    <BaseCard>
      <BaseCardHeader
        isLoading={isLoading}
        leftIconProps={{
          iconName: 'Bed',
        }}
        title={intl.formatMessage(messages.bedActivity)}
        subtitle={intl.formatMessage(messages.bedActivitySubtitle)}
        titleClassName="text-base"
        rightContentClassName={'items-start'}
        className={'items-start'}
      >
        <StyledDescription>
          {!isNullOrUndefined(sleepWakeSettings?.startHour) && (
            <span>
              {intl.formatMessage(messages.bedActivityRightDesc, {
                startTime: displayInTimezone(
                  dayjs()
                    .utc()
                    .hour(sleepWakeSettings?.startHour ?? 12),
                  timezone,
                  HOUR_12H_FORMAT,
                ),
              })}
            </span>
          )}
          {hasSleepWakeFeature &&
            !isNullOrUndefined(sleepWakeSettings?.startHour) &&
            !isNullOrUndefined(sleepWakeSettings?.endHour) && (
              <span>
                {intl.formatMessage(messages.bedActivitySleepInfo, {
                  startTime: displayInTimezone(
                    dayjs()
                      .utc()
                      .hour(sleepWakeSettings?.startHour ?? 12),
                    timezone,
                    HOUR_12H_FORMAT,
                  ),
                  endTime: displayInTimezone(
                    dayjs()
                      .utc()
                      .hour(sleepWakeSettings?.endHour ?? 7),
                    timezone,
                    HOUR_12H_FORMAT,
                  ),
                })}
              </span>
            )}
        </StyledDescription>
      </BaseCardHeader>
      <div className="flex flex-row justify-center">
        <StackedBarsWithLineComposedChart
          isLoading={isLoading}
          barGap={100}
          data={daysArray.map(day => ({
            day: dayjs(
              day,
              getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT),
            ).format(getFormatDateByContinent(MONTH_DAY_FORMAT)),
            remSleep: sleepData?.[day]?.remSleep ?? 0,
            sleep: sleepData?.[day]?.sleep ?? 0,
            highMotion: motionData?.[day]?.highMotion ?? 0,
            lowMotion: motionData?.[day]?.lowMotion ?? 0,
            mediumMotion: motionData?.[day]?.mediumMotion ?? 0,
            total: hoursInBedPerDay?.[day]?.toFixed(1) ?? 0,
            barHeightReference: 24,
          }))}
          config={getWidgetConfig(intl, hasSleepWakeFeature)}
          legendDescription={intl.formatMessage(messages.bedActivityLegend)}
          leftAxis={{
            domain: [0, 24],
            tickCount: 12,
            unit: intl.formatMessage(messages.hour),
            dx: -10,
          }}
        />
      </div>
    </BaseCard>
  );
};

export default Connector(injectIntl(BedActivity));
